import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { addToast, ToastContent } from '@mobi/component-library/Feedback/Toast'
import { SvgLazy } from '@mobi/component-library/Common/Svg/SvgLazy'
import { AppRoutes } from '@core/AppRoutes'
import { setBetSlipNewIsOpen } from '@core/Areas/AppHeader/Store'
import { postShareToFollowMe } from '@core/Areas/FollowMe/helpers/api'
import { followMePrincipleProfileQuery } from '@core/Areas/FollowMe/helpers/queries'
import { ProfileAvatar } from '@core/Areas/FollowMe/Components/Common/ProfileAvatar'
import type { ShareBetData } from '@core/Areas/ShareBet/types'
import { handleApiErrorNotification } from '@core/Areas/FollowMe/helpers/handleApiErrorNotification'
import { trackShareBetClick } from '@core/Areas/ShareBet/ShareBetOutgoing/Components/ShareWithExternal/analytics'
import { ExpertHubFirstPostModal } from '../ExpertHubFirstPostModal'

export const ExpertHubButton: React.FC<{
  data: ShareBetData
  dismissTray: () => void
}> = ({ data, dismissTray }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const [isFirstPostModalOpen, setFirstPostModalOpen] = useState(false)

  const { data: profileData, isLoading: isLoadingProfile } = useQuery(followMePrincipleProfileQuery)

  const { mutate } = useMutation({
    mutationFn: postShareToFollowMe,
    onSuccess: () => {
      addToast({
        id: 'addto-sharehub-success',
        isDismissable: true,
        content: <ToastContent title='Bet was posted to Expert Hub' icon='success' />,
      })
      queryClient.invalidateQueries(followMePrincipleProfileQuery)
      trackShareBetClick(data, false, 'follow-me')
    },
    onError: async err => {
      const fallbackMsg = 'Unable to share to Expert Hub, please try again'
      handleApiErrorNotification(err, fallbackMsg)
    },
  })

  const postToExpertHub = () => {
    if (profileData) {
      mutate({ ticketSequence: data.share.ticketSequence as number })
    } else {
      dispatch(setBetSlipNewIsOpen(false))
      navigate(AppRoutes.FollowMe)
    }
  }

  const handleExpertHubClick = () => {
    if (profileData?.shareCount === 0) {
      setFirstPostModalOpen(true)
    } else {
      dismissTray()
      postToExpertHub()
    }
  }

  const handleFirstPostConfirm = () => {
    setFirstPostModalOpen(false)
    dismissTray()
    postToExpertHub()
  }

  return (
    <>
      <button onClick={handleExpertHubClick} disabled={isLoadingProfile} type='button'>
        {profileData ? (
          <ProfileAvatar color={profileData.color} avatar={profileData.avatar} />
        ) : (
          <SvgLazy name='ExpertHubLogo' width='7.2rem' color='' />
        )}
        Post to Expert Hub
      </button>

      <ExpertHubFirstPostModal
        isOpen={isFirstPostModalOpen}
        onConfirm={handleFirstPostConfirm}
        onCancel={() => setFirstPostModalOpen(false)}
      />
    </>
  )
}
